import React, { useState, useCallback, useMemo, useEffect } from "react"
import { Upload, Button, Tooltip } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { RcFile } from "antd/es/upload/interface"
import {
  faUpload,
  faTrash,
  faImage,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import ResizableElementWrapper from "./shared/ResizableElementWrapper.component"
import {
  DEFAULT_IMAGE_WIDTH,
  DEFAULT_IMAGE_HEIGHT,
} from "constants/dashboardElementSizes"
import { fileToBase64 } from "utils/files/fileToBase64"
import {
  SlideElement,
  ImageSlideElementProps,
  ELEMENT_TYPE,
  ImageDimensions,
} from "types/ReportingDashboard/elements.types"

/**
 * Element update payload for image elements
 */
interface ImageElementUpdate {
  imageSrc?: string | null
  width?: number
  height?: number
}

/**
 * Props for ImageElement component
 */
export interface ImageElementProps {
  /** Element object from parent - should be an image element */
  element: SlideElement
  /** Callback to update element props in parent */
  onUpdate: (elementId: string, updates: ImageElementUpdate) => void
  /** Flag to disable editing */
  readonly?: boolean
  /** Callback to request deletion of this element */
  onDeleteRequest?: () => void
}

/**
 * Helper to get image dimensions from a Base64 source.
 *
 * @param base64Src - Base64 encoded image source
 * @returns Promise resolving to image dimensions
 */
function getImageDimensionsFromSrc(
  base64Src: string
): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = (err) => reject(err)
    img.src = base64Src
  })
}

/**
 * Renders an image element within the ReportingCanvas.
 * Allows uploading an image, displaying it, resizing, and requesting deletion.
 * Dynamically adjusts container aspect ratio to match the image via ResizableElementWrapper.
 *
 * @param props - Component props
 * @returns JSX element for image upload, display, and management
 *
 * @example
 * <ImageElement
 *   element={imageElement}
 *   onUpdate={handleUpdate}
 *   readonly={false}
 *   onDeleteRequest={handleDelete}
 * />
 */
const ImageElement: React.FC<ImageElementProps> = ({
  element,
  onUpdate,
  readonly = false,
  onDeleteRequest,
}) => {
  const [uploadLoading, setUploadLoading] = useState<boolean>(false)
  const [naturalDims, setNaturalDims] = useState<ImageDimensions | null>(null) // { width, height } of the loaded image
  const [dimLoading, setDimLoading] = useState<boolean>(false) // Loading state for image dimensions

  // Type guard to check if element is an image element
  const isImageElement = (el: SlideElement): el is ImageSlideElementProps => {
    return el.type === ELEMENT_TYPE.IMAGE
  }

  // Type-safe property access for image elements
  const imageElement = isImageElement(element) ? element : null
  const imageUrl = imageElement?.imageSrc || null
  const elementId = element.id
  const alt = "" // ImageSlideElementProps doesn't have alt property in the original types

  // Calculate aspect ratio for the hook, to be passed to ResizableElementWrapper
  const calculatedAspectRatio = useMemo((): number | undefined => {
    if (naturalDims && naturalDims.width > 0 && naturalDims.height > 0) {
      return naturalDims.height / naturalDims.width
    }
    // If no naturalDims or invalid, don't enforce aspect ratio initially.
    // The wrapper will use current element.width/height or defaults.
    return undefined
  }, [naturalDims])

  // Effect to load natural dimensions when imageUrl or elementId changes
  useEffect(() => {
    if (imageUrl && imageUrl.startsWith("data:image")) {
      setDimLoading(true)
      setNaturalDims(null)
      let isMounted = true

      getImageDimensionsFromSrc(imageUrl)
        .then((dims: ImageDimensions) => {
          if (isMounted) {
            if (dims.width > 0 && dims.height > 0) {
              setNaturalDims(dims)
            } else {
              console.warn(
                `Image ${elementId} loaded with invalid dimensions:`,
                dims
              )
              setNaturalDims(null)
              notify.toast.error("Image dimensions could not be determined.")
            }
          }
        })
        .catch((error: Error) => {
          console.error("Error loading image dimensions:", error)
          if (isMounted) {
            setNaturalDims(null)
            notify.toast.error("Could not load image details.")
          }
        })
        .finally(() => {
          if (isMounted) {
            setDimLoading(false)
          }
        })

      return () => {
        isMounted = false
      }
    } else {
      setNaturalDims(null)
      setDimLoading(false)
    }
  }, [imageUrl, elementId])

  const handleBeforeUpload = useCallback(
    async (file: RcFile): Promise<boolean | typeof Upload.LIST_IGNORE> => {
      if (readonly) {
        notify.toast.info("Cannot upload images in read-only mode.")
        return false
      }

      const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png"
      if (!isJpgOrPng) {
        notify.toast.error("You can only upload JPG/PNG file!")
        return Upload.LIST_IGNORE
      }

      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        notify.toast.error("Image must be smaller than 2MB!")
        return Upload.LIST_IGNORE
      }

      setUploadLoading(true)
      setNaturalDims(null) // Clear old dimensions
      setDimLoading(true) // Indicate dimension loading

      try {
        const base64Url = (await fileToBase64(file, true)) as string
        // When uploading a new image, if we have natural dims from a previous image,
        // it's good to reset the element's height to a default or let the wrapper adjust.
        // The `onUpdate` for imageSrc will trigger useEffect to get new naturalDims.
        // The ResizableElementWrapper's aspectRatio prop will update, and it should adjust.
        onUpdate(elementId, {
          imageSrc: base64Url,
          height: DEFAULT_IMAGE_HEIGHT,
        }) // Reset height to default
      } catch (error) {
        notify.toast.error("Failed to read file.")
        console.error("File reading error in ImageElement:", error)
        setDimLoading(false)
      } finally {
        setUploadLoading(false)
      }

      return false
    },
    [onUpdate, elementId, readonly]
  )

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      if (!readonly && onDeleteRequest) {
        onDeleteRequest()
      }
    },
    [onDeleteRequest, readonly]
  )

  const handleRemoveImage = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      if (!readonly) {
        setNaturalDims(null)
        onUpdate(elementId, {
          imageSrc: null,
          width: DEFAULT_IMAGE_WIDTH,
          height: DEFAULT_IMAGE_HEIGHT,
        })
      }
    },
    [onUpdate, elementId, readonly]
  )

  // --- Styles ---

  const contentContainerStyle = useMemo((): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden", // Important for the image content
      backgroundColor: imageUrl && naturalDims ? "transparent" : "#f9fafb", // gray-50 for placeholder
      border: imageUrl && naturalDims ? "1px dashed #cbd5e0" : "none", // gray-300 border for loaded image
      position: "relative", // For absolute positioned image if needed
    }

    if (imageUrl && naturalDims) {
      return {
        ...baseStyle,
      }
    }
    // Placeholder or loading state
    return baseStyle
  }, [imageUrl, naturalDims])

  return (
    <ResizableElementWrapper
      element={element}
      onUpdate={onUpdate}
      readonly={readonly}
      minDimensions={{ width: 50, height: 50 }}
      defaultWidth={DEFAULT_IMAGE_WIDTH}
      defaultHeight={DEFAULT_IMAGE_HEIGHT}
      maintainAspectRatio={!!(naturalDims && calculatedAspectRatio)}
      aspectRatio={calculatedAspectRatio}
      additionalClasses={`image-element-wrapper ${
        imageUrl && naturalDims ? "has-image-dims" : "no-image-dims"
      }`}
      styleOverrides={{
        cursor: readonly ? "default" : "pointer",
      }}
      ariaLabel="Image element"
    >
      {/* This div is the direct child of ResizableElementWrapper and will fill it. */}
      {/* It handles the internal layout of the image or placeholder. */}
      <div style={contentContainerStyle}>
        {imageUrl ? (
          dimLoading ? (
            <div className="flex flex-col items-center justify-center text-gray-400">
              <FontAwesomeIcon icon={faSpinner} spin size="lg" />
              <span className="text-xs mt-2">Loading image...</span>
            </div>
          ) : naturalDims ? (
            // Image is displayed here. objectFit will handle aspect ratio within this container.
            // The container itself fills the ResizableElementWrapper.
            <img
              src={imageUrl}
              alt={alt || "Uploaded image"}
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain", // Ensures aspect ratio is maintained and image fits
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-red-500 p-2 text-center">
              <FontAwesomeIcon icon={faImage} size="lg" className="mb-1" />
              <span className="text-xs">Error loading image details</span>
            </div>
          )
        ) : (
          <Upload
            name="image"
            listType="picture-card"
            className="image-uploader no-border"
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            disabled={readonly || uploadLoading}
            accept="image/png, image/jpeg"
          >
            <div className="flex flex-col items-center justify-center p-2">
              <FontAwesomeIcon
                icon={uploadLoading ? faSpinner : faUpload}
                spin={uploadLoading}
                className="text-gray-400 text-lg mb-1"
              />
              <div className="text-xs text-gray-500">
                {uploadLoading ? "Processing..." : "Upload"}
              </div>
            </div>
          </Upload>
        )}

        {!readonly && (
          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            {imageUrl && naturalDims && (
              <Tooltip title="Remove Image">
                <Button
                  type="primary"
                  danger
                  ghost
                  size="small"
                  icon={<FontAwesomeIcon icon={faTrash} size="xs" />}
                  onClick={handleRemoveImage}
                  style={{ minWidth: "24px", padding: "0 5px" }}
                  aria-label="Remove current image"
                />
              </Tooltip>
            )}
            {onDeleteRequest && (
              <Tooltip title="Delete Element">
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  onClick={handleDelete}
                  style={{ minWidth: "24px", padding: "0 5px" }}
                  aria-label="Delete image element"
                />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </ResizableElementWrapper>
  )
}

export default ImageElement
